<template>
  <div>
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h2>Feuilles de Ramassage</h2>
      <b-button
        variant="primary"
        :to="{ name: 'create-pickup-roadmap' }"
      >
        <feather-icon
          icon="PlusIcon"
          class="mr-50"
        />
        Nouvelle Feuille
      </b-button>
    </div>

    <!-- Filters -->
    <b-card class="mb-2">
      <b-row>
        <b-col
          md="3"
          sm="6"
        >
          <b-form-group label="Statut">
            <v-select
              v-model="filters.status"
              :options="statusOptions"
              :reduce="option => option.value"
              placeholder="Tous les statuts"
              @input="loadPickupRoadmaps"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="3"
          sm="6"
        >
          <b-form-group label="Recherche">
            <b-form-input
              v-model="filters.search"
              placeholder="Rechercher..."
              @input="debounceSearch"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Table -->
    <b-card>
      <div
        v-if="isLoading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <b-table
        v-else
        responsive
        :items="pickupRoadmaps"
        :fields="fields"
        show-empty
        empty-text="Aucune feuille de ramassage trouvée"
        class="mb-0"
      >
        <!-- ID Column -->
        <template #cell(id)="data">
          <router-link :to="{ name: 'show-pickup-roadmap', params: { id: data.item.id } }">
            #{{ data.item.id }}
          </router-link>
        </template>

        <!-- Status Column -->
        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant(data.item.status)">
            {{ data.item.status_display }}
          </b-badge>
        </template>

        <!-- Orders Count Column -->
        <template #cell(orders_count)="data">
          <b-badge variant="light-primary">
            {{ data.item.orders_count }} colis
          </b-badge>
        </template>

        <!-- Date Column -->
        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>

        <!-- Actions Column -->
        <template #cell(actions)="data">
          <div class="d-flex">
            <b-button
              v-b-tooltip.hover
              title="Voir détails"
              variant="outline-primary"
              size="sm"
              :to="{ name: 'show-pickup-roadmap', params: { id: data.item.id } }"
            >
              <feather-icon icon="EyeIcon" />
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Pagination -->
      <div class="d-flex justify-content-between align-items-center mt-2">
        <span class="text-muted">
          Total: {{ totalCount }} feuilles
        </span>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="perPage"
          first-number
          last-number
          @change="loadPickupRoadmaps"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BTable,
  BBadge,
  BPagination,
  BSpinner,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'PickupRoadmapIndex',
  components: {
    BCard,
    BButton,
    BTable,
    BBadge,
    BPagination,
    BSpinner,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      isLoading: false,
      pickupRoadmaps: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 20,

      filters: {
        status: null,
        search: '',
      },

      statusOptions: [
        { label: 'Tous', value: null },
        { label: 'En attente', value: 'pending' },
        { label: 'En cours', value: 'in_progress' },
        { label: 'Terminé', value: 'completed' },
        { label: 'Annulé', value: 'cancelled' },
      ],

      fields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'driver_name', label: 'Livreur', sortable: true },
        { key: 'driver_phone', label: 'Téléphone' },
        { key: 'repository_name', label: 'Dépôt', sortable: true },
        { key: 'car_info', label: 'Voiture' },
        { key: 'orders_count', label: 'Colis', sortable: true },
        { key: 'status', label: 'Statut', sortable: true },
        { key: 'created_at', label: 'Date', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],

      searchTimeout: null,
    }
  },
  created() {
    this.loadPickupRoadmaps()
  },
  methods: {
    async loadPickupRoadmaps() {
      this.isLoading = true
      try {
        const params = {
          page: this.currentPage,
          page_size: this.perPage,
        }

        if (this.filters.status) {
          params.status = this.filters.status
        }

        if (this.filters.search) {
          params.search = this.filters.search
        }

        const { data } = await axios.get('/api/pickups/roadmap/', { params })
        this.pickupRoadmaps = data.results || data
        this.totalCount = data.count || data.length
      } catch (error) {
        console.error('Error loading pickup roadmaps:', error)
        this.showToast('danger', 'Erreur lors du chargement')
      } finally {
        this.isLoading = false
      }
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.loadPickupRoadmaps()
      }, 500)
    },

    async startPickup(id) {
      try {
        await axios.post(`/api/pickups/roadmap/${id}/start/`)
        this.showToast('success', 'Feuille de ramassage démarrée')
        this.loadPickupRoadmaps()
      } catch (error) {
        this.showToast('danger', error.response?.data?.detail || 'Erreur')
      }
    },

    async completePickup(id) {
      const confirmed = await this.$swal({
        title: 'Confirmer',
        text: 'Voulez-vous marquer cette feuille comme terminée ?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui',
        cancelButtonText: 'Non',
      })

      if (!confirmed.isConfirmed) return

      try {
        await axios.post(`/api/pickups/roadmap/${id}/complete/`)
        this.showToast('success', 'Feuille de ramassage terminée')
        this.loadPickupRoadmaps()
      } catch (error) {
        this.showToast('danger', error.response?.data?.detail || 'Erreur')
      }
    },

    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        in_progress: 'info',
        completed: 'success',
        cancelled: 'danger',
      }
      return variants[status] || 'secondary'
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: 'InfoIcon',
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
